// Admin: attach a PDF / PPT / DOC to a lesson.
// - For lessons whose type is pdf/ppt/pptx/doc/docx the file becomes the
//   lesson's primary `content_file_path`.
// - Otherwise it's stored as a "resource" attachment (multiple allowed).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Upload, X, Trash2, Download } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/api";

const DOC_EXTS = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"];
const RESOURCE_EXTS = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx", "txt", "png", "jpg", "jpeg", "zip"];
const PRIMARY_LESSON_TYPES = new Set(["pdf", "doc", "docx", "ppt", "pptx"]);

const fmtBytes = (n) => {
  if (!n) return "";
  const kb = n / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};
const backend = API_BASE.replace(/\/api$/, "");

export function AddDocumentDialog({ open, onOpenChange, lesson, onSuccess }) {
  const [file, setFile] = useState(null);
  const [resourceTitle, setResourceTitle] = useState("");
  const [downloadAllowed, setDownloadAllowed] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [resources, setResources] = useState([]);
  const [loadingRes, setLoadingRes] = useState(false);
  const primaryMode = PRIMARY_LESSON_TYPES.has((lesson?.lesson_type || "").toLowerCase());

  useEffect(() => {
    if (!open) return;
    setFile(null); setResourceTitle(""); setDownloadAllowed(true);
    loadRes();
  }, [open, lesson?.id]);

  const loadRes = async () => {
    if (!lesson?.id) return;
    setLoadingRes(true);
    try {
      const { data } = await api.get(`/admin/lessons/${lesson.id}/resources`);
      setResources(data.data?.resources || []);
    } catch { /* silent */ }
    finally { setLoadingRes(false); }
  };

  const submit = async () => {
    if (!file) { toast.error("Please pick a file to upload."); return; }
    const ext = (file.name || "").toLowerCase().split(".").pop();
    if (primaryMode ? !DOC_EXTS.includes(ext) : !RESOURCE_EXTS.includes(ext)) {
      toast.error(`Allowed: ${(primaryMode ? DOC_EXTS : RESOURCE_EXTS).join(", ")}`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (primaryMode) {
        await api.post(`/admin/lessons/${lesson.id}/upload-document`, form,
          { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Document set as lesson content.");
      } else {
        form.append("resource_title", resourceTitle.trim() || file.name);
        form.append("download_allowed", downloadAllowed ? "true" : "false");
        await api.post(`/admin/lessons/${lesson.id}/resources`, form,
          { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Resource added.");
      }
      if (typeof onSuccess === "function") onSuccess();
      if (primaryMode) onOpenChange(false);
      else { setFile(null); setResourceTitle(""); loadRes(); }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const removeResource = async (r) => {
    if (!window.confirm(`Remove "${r.resource_title || r.original_filename}"?`)) return;
    try {
      await api.delete(`/admin/lesson-resources/${r.id}`);
      toast.success("Resource removed.");
      loadRes();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="admin-add-document-modal">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-3">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <FileText className="h-4 w-4 text-[#101876]" />
            {primaryMode ? "Upload lesson document" : "Add resource / handout"}
          </div>
          <button type="button" onClick={() => onOpenChange(false)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          {lesson && (
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
              Lesson: <b className="text-slate-800">{lesson.lesson_title}</b>
              <span className="ml-1 rounded bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-600">{lesson.lesson_type}</span>
            </div>
          )}

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              File · {(primaryMode ? DOC_EXTS : RESOURCE_EXTS).join(", ")}
            </label>
            <input
              type="file"
              accept={(primaryMode ? DOC_EXTS : RESOURCE_EXTS).map((e) => `.${e}`).join(",")}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="doc-file-input"
              className="mt-1 block w-full text-xs file:mr-3 file:rounded-md file:border-0 file:bg-[#101876] file:px-3 file:py-1.5 file:text-[11px] file:font-bold file:text-white hover:file:bg-[#0c135c]"
            />
            {file && <div className="mt-1 text-[11px] text-slate-500">{file.name} · {fmtBytes(file.size)}</div>}
          </div>

          {!primaryMode && (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Title (optional)</label>
                <input value={resourceTitle} onChange={(e) => setResourceTitle(e.target.value)}
                       data-testid="doc-title-input"
                       placeholder="Defaults to file name…"
                       className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#101876]" />
              </div>
              <label className="flex items-center gap-2 text-[11px] text-slate-600">
                <input type="checkbox" checked={downloadAllowed} onChange={(e) => setDownloadAllowed(e.target.checked)} data-testid="doc-download-allowed" />
                Allow learners to download this resource
              </label>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold">Cancel</button>
            <button type="button" onClick={submit} disabled={uploading || !file}
                    data-testid="doc-upload-submit"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#101876] px-4 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c] disabled:opacity-40">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploading ? "Uploading…" : primaryMode ? "Set as content" : "Add resource"}
            </button>
          </div>
        </div>

        {!primaryMode && (
          <div className="border-t border-slate-100 bg-slate-50/50 p-3">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Attached resources ({resources.length})
            </div>
            {loadingRes ? (
              <div className="flex items-center gap-2 p-2 text-[11px] text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> loading…</div>
            ) : resources.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-300 bg-white p-3 text-center text-[11px] text-slate-400">
                No resources attached yet.
              </div>
            ) : (
              <div className="space-y-1.5">
                {resources.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 rounded-md bg-white px-2.5 py-1.5 text-[11px]" data-testid={`doc-resource-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-slate-800">{r.resource_title || r.original_filename}</div>
                      <div className="text-[10px] text-slate-500 uppercase">{r.resource_type}</div>
                    </div>
                    {r.download_allowed && r.file_path && (
                      <a href={`${backend}/${r.file_path}`} target="_blank" rel="noreferrer"
                         className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:border-[#101876]/40 hover:text-[#101876]"
                         title="Download">
                        <Download className="h-3 w-3" />
                      </a>
                    )}
                    <button type="button" onClick={() => removeResource(r)}
                            data-testid={`doc-resource-remove-${r.id}`}
                            className="rounded-md border border-transparent p-1 text-slate-400 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
